import { useState } from "react";
import axios from "axios";
import { Send, Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { SITE } from "../lib/site";
import { useT } from "../lib/i18n";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm({ defaultServiceIndex, introText }) {
  const { t } = useT();
  const SERVICES = t("contact.services");
  const defaultService = defaultServiceIndex != null ? String(defaultServiceIndex) : "";
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t("contact.errorMissing"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contacts`, { ...form, service: SERVICES[form.service] || "" });
      trackEvent("generate_lead", { form_name: "contact", ...(SERVICES[form.service] && { service: SERVICES[form.service] }) });
      toast.success(t("contact.toastOk"));
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", service: defaultService, message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || t("contact.errorGeneric");
      toast.error(typeof detail === "string" ? detail : "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-sm tracking-[0.25em] uppercase text-[#1B5E20] font-medium mb-4">
              {t("contact.eyebrow")}
            </div>
            <h2 className="font-['Outfit'] text-4xl sm:text-5xl font-light tracking-tight text-[#1A2E1C] leading-tight">
              {t("contact.title1")}{" "}
              <span className="text-[#1B5E20] font-medium">{t("contact.title2")}</span>
            </h2>
            <p className="mt-6 text-lg text-[#5C705E] leading-relaxed">
              {introText || t("contact.intro")}
            </p>

            <div className="mt-10 space-y-5">
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="contact-info-phone"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1B5E20] transition-colors">
                  <Phone className="w-5 h-5 text-[#1B5E20] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C705E]">
                    {t("contact.phoneLabel")}
                  </div>
                  <div className="font-['Outfit'] font-semibold text-lg text-[#1A2E1C]">
                    {SITE.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-info-email"
                className="flex items-start gap-4 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1B5E20] transition-colors">
                  <Mail className="w-5 h-5 text-[#1B5E20] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C705E]">
                    {t("contact.emailLabel")}
                  </div>
                  <div className="font-['Outfit'] font-semibold text-lg text-[#1A2E1C] break-all">
                    {SITE.email}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#1B5E20]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#5C705E]">
                    {t("contact.zoneLabel")}
                  </div>
                  <div className="font-['Outfit'] font-semibold text-lg text-[#1A2E1C]">
                    {t("contact.zoneValue")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-white rounded-3xl p-8 md:p-10 border border-[#E5EBE5] shadow-sm">
              {success ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-[#E8F5E9] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-[#1B5E20]" />
                  </div>
                  <h3 className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C] mb-3">
                    {t("contact.successTitle")}
                  </h3>
                  <p className="text-[#5C705E] mb-6">{t("contact.successText")}</p>
                  <Button
                    onClick={() => setSuccess(false)}
                    data-testid="new-request-button"
                    variant="outline"
                    className="border-[#1B5E20] text-[#1B5E20] hover:bg-[#E8F5E9] rounded-full px-6"
                  >
                    {t("contact.newRequest")}
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-[#1A2E1C] mb-2 block">
                        {t("contact.nameLabel")} <span className="text-[#D32F2F]">*</span>
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-name-input"
                        value={form.name}
                        onChange={(e) => update("name", e.target.value)}
                        placeholder={t("contact.namePh")}
                        className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl h-12 focus-visible:ring-[#4CAF50]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#1A2E1C] mb-2 block">
                        {t("contact.emailFieldLabel")} <span className="text-[#D32F2F]">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder={t("contact.emailPh")}
                        className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl h-12 focus-visible:ring-[#4CAF50]"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone" className="text-[#1A2E1C] mb-2 block">
                        {t("contact.phoneFieldLabel")}
                      </Label>
                      <Input
                        id="phone"
                        data-testid="contact-phone-input"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder={t("contact.phonePh")}
                        className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl h-12 focus-visible:ring-[#4CAF50]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service" className="text-[#1A2E1C] mb-2 block">
                        {t("contact.serviceLabel")}
                      </Label>
                      <Select value={form.service} onValueChange={(v) => update("service", v)}>
                        <SelectTrigger
                          id="service"
                          data-testid="contact-service-select"
                          className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl h-12 focus:ring-[#4CAF50]"
                        >
                          <SelectValue placeholder={t("contact.servicePh")} />
                        </SelectTrigger>
                        <SelectContent>
                          {SERVICES.map((s, i) => (
                            <SelectItem key={s} value={String(i)} data-testid={`service-option-${s}`}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#1A2E1C] mb-2 block">
                      {t("contact.messageLabel")} <span className="text-[#D32F2F]">*</span>
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder={t("contact.messagePh")}
                      rows={5}
                      className="bg-[#FDFDFC] border-[#E5EBE5] rounded-xl focus-visible:ring-[#4CAF50] resize-none"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="w-full bg-[#1B5E20] hover:bg-[#164d1a] text-white rounded-full h-14 text-base font-medium group transition-all hover:-translate-y-0.5 disabled:opacity-60"
                  >
                    {loading ? (
                      t("contact.submitting")
                    ) : (
                      <>
                        {t("contact.submit")}
                        <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-[#5C705E] text-center">{t("contact.rgpd")}</p>

                  <div className="relative flex items-center gap-3 py-1">
                    <div className="flex-1 h-px bg-[#E5EBE5]" />
                    <span className="text-xs uppercase tracking-widest text-[#5C705E]">
                      {t("contact.or")}
                    </span>
                    <div className="flex-1 h-px bg-[#E5EBE5]" />
                  </div>

                  <a
                    href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent(t("contact.waMessage"))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-cta"
                    className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full h-14 font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                      <path d="M17.6 6.31A7.85 7.85 0 0 0 12.05 4a7.94 7.94 0 0 0-6.88 11.93L4 20l4.2-1.1a7.94 7.94 0 0 0 3.85 1h.01a7.93 7.93 0 0 0 7.94-7.93 7.88 7.88 0 0 0-2.4-5.66Zm-5.55 12.2h-.01a6.6 6.6 0 0 1-3.36-.92l-.24-.14-2.5.66.67-2.43-.16-.25a6.6 6.6 0 0 1 5.6-10.1 6.55 6.55 0 0 1 4.66 1.93 6.55 6.55 0 0 1 1.93 4.66 6.6 6.6 0 0 1-6.59 6.59Zm3.62-4.94c-.2-.1-1.18-.58-1.36-.65-.18-.07-.31-.1-.45.1-.13.2-.51.65-.62.78-.12.13-.23.15-.42.05a5.4 5.4 0 0 1-1.6-.99 6 6 0 0 1-1.1-1.38c-.12-.2 0-.3.09-.4.09-.1.2-.23.3-.34.1-.11.13-.2.2-.33.06-.13.03-.25-.02-.35-.05-.1-.45-1.09-.62-1.5-.16-.39-.33-.34-.45-.34l-.39-.01a.75.75 0 0 0-.54.25c-.19.2-.71.7-.71 1.7s.73 1.97.83 2.1c.1.14 1.43 2.18 3.46 3.06.49.21.87.34 1.17.43.49.16.93.13 1.28.08.39-.06 1.18-.48 1.35-.95.16-.46.16-.86.12-.94-.05-.09-.18-.14-.38-.24Z" />
                    </svg>
                    {t("contact.waCta")}
                  </a>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
